import React, { useState } from "react";
import { Code, LayoutList, RotateCw } from "lucide-react";
import { cn } from "lib/utils";

import useGetEventStatus from "../../hooks/useGetEventStatus";
import useGetEventSchedule from "../../hooks/useGetEventSchedule";
import LoadingSpinner from "../components/LoadingSpinner";
import { Spinner } from "components/ui/spinner";
import { Button } from "components/ui/button";
import StatusView from "./StatusView";
import ScheduleFormatted from "./ScheduleFormatted";
import RawView from "./RawView";

const SUB_TABS = [
    { key: "status", label: "Status" },
    { key: "schedule", label: "Schedule" },
];

function EventDetails({ eventId, environment }) {
    // Both are fetched once on mount and cached here; switching sub-tabs never
    // refetches (this component stays mounted). The header refresh button
    // refetches whichever sub-tab is active.
    const status = useGetEventStatus(eventId, environment);
    const schedule = useGetEventSchedule(eventId, environment);
    const [sub, setSub] = useState("status");
    const [view, setView] = useState("formatted");

    const active = sub === "status" ? status : schedule;
    const refetchActive =
        sub === "status" ? status.refetchEventStatus : schedule.refetchEventSchedule;

    return (
        <div className="mt-2">
            <div className="flex flex-wrap items-center gap-3">
                <div className="inline-flex rounded-lg border border-border bg-muted/50 p-0.5">
                    {SUB_TABS.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            onClick={() => setSub(tab.key)}
                            className={cn(
                                "rounded-md px-4 py-1.5 text-sm font-medium transition-colors",
                                sub === tab.key
                                    ? "bg-card text-foreground shadow-sm"
                                    : "text-muted-foreground hover:text-foreground"
                            )}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                <Button
                    size="icon-sm"
                    variant="outline"
                    onClick={refetchActive}
                    title="Refresh"
                    disabled={active.isRefetching}
                >
                    <RotateCw />
                </Button>
                {active.isRefetching && <Spinner className="h-4 w-4" />}

                <div className="ml-auto inline-flex overflow-hidden rounded-md border border-border">
                    <ToggleButton active={view === "formatted"} onClick={() => setView("formatted")}>
                        <LayoutList className="h-3.5 w-3.5" /> Formatted
                    </ToggleButton>
                    <ToggleButton active={view === "raw"} onClick={() => setView("raw")}>
                        <Code className="h-3.5 w-3.5" /> Raw
                    </ToggleButton>
                </div>
            </div>

            {sub === "status" && (
                <TabContent
                    result={status}
                    data={status.eventStatus}
                    view={view}
                    Formatted={StatusView}
                    rawFileName="live_event_status.json"
                    label="status"
                />
            )}

            {sub === "schedule" && (
                <TabContent
                    result={schedule}
                    data={schedule.eventSchedule}
                    view={view}
                    Formatted={ScheduleFormatted}
                    rawFileName="event_schedule.json"
                    label="schedule"
                />
            )}
        </div>
    );
}

function TabContent({ result, data, view, Formatted, rawFileName, label }) {
    if (!result.isLoaded) {
        return <LoadingSpinner />;
    }
    if (result.error) {
        return (
            <div className="mt-4 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                <strong>Warning!</strong>{" "}
                {typeof result.error === "string" ? result.error : `Failed to load event ${label}.`}
            </div>
        );
    }
    return view === "raw" ? (
        <RawView raw={data?.raw} fileName={rawFileName} />
    ) : (
        <Formatted formatted={data?.formatted} />
    );
}

function ToggleButton({ active, onClick, children }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                "inline-flex items-center gap-1.5 px-3.5 py-1.5 text-[13px] font-medium transition-colors",
                active
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground"
            )}
        >
            {children}
        </button>
    );
}

export default EventDetails;
